import { useState, useEffect, useRef } from 'react';
import { useDeckStore } from '../../stores/deck-store.js';
import { useSearchStore } from '../../stores/search-store.js';
import { useTagProfile } from '../../hooks/useTagProfile.js';
import { TagTree } from './TagTree.js';

function useElapsedTimer(running: boolean): number {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);

  useEffect(() => {
    if (!running) { setElapsed(0); return; }
    startRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [running]);

  return elapsed;
}

export function TagProfile() {
  const tagProfile = useDeckStore((s) => s.tagProfile);
  const tagHierarchy = useDeckStore((s) => s.tagHierarchy);
  const isProfileStale = useDeckStore((s) => s.isProfileStale);
  const isProfileLoading = useDeckStore((s) => s.isProfileLoading);
  const entries = useDeckStore((s) => s.entries);
  const toggleTagFilter = useSearchStore((s) => s.toggleTagFilter);
  const clearSearch = useSearchStore((s) => s.clearSearch);
  const activeTagSlugs = useSearchStore((s) => s.activeTagSlugs);
  const { analyze } = useTagProfile();
  const elapsed = useElapsedTimer(isProfileLoading);

  const hasCards = entries.length > 0;

  if (!hasCards) return null;

  // Estimate: ~10 GraphQL requests for 100 cards (batches of 10), ~2s each
  const cardCount = entries.reduce((sum, e) => sum + e.quantity, 0);
  const estimatedSeconds = Math.max(5, Math.ceil(cardCount / 10) * 2);

  // Flat list sorted by count descending, single-occurrence and zero-match hidden
  const visibleEntries = tagProfile?.entries.filter((e) => e.matchCount > 1) ?? [];

  return (
    <div className="px-4 py-3 border-b border-gray-800">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tag Profile</h2>
        <div className="flex items-center gap-2">
          {tagProfile && isProfileStale && (
            <button
              onClick={() => { void analyze(); }}
              className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Re-analyze ↻
            </button>
          )}
          {tagProfile && !isProfileLoading && (
            <button
              onClick={() => { void analyze(true); }}
              title="Force refresh (bypass cache)"
              className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
            >
              ⟳
            </button>
          )}
        </div>
      </div>

      {!tagProfile && !isProfileLoading && (
        <div className="space-y-2">
          {isProfileStale && (
            <p className="text-xs text-yellow-500 italic">Deck changed since last analysis.</p>
          )}
          <p className="text-xs text-gray-500">Loading tag profile…</p>
        </div>
      )}

      {isProfileLoading && (
        <div className="flex items-center gap-2 py-2">
          <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0" />
          <p className="text-xs text-gray-400">
            Fetching tags for {cardCount} cards… ({elapsed}s / ~{estimatedSeconds}s)
          </p>
        </div>
      )}

      {tagProfile && !isProfileLoading && (
        tagHierarchy ? (
          <TagTree hierarchy={tagHierarchy} />
        ) : (
          <div className="space-y-0.5">
            {visibleEntries.map((entry) => {
              const isActive = activeTagSlugs.includes(entry.tag.slug);
              return (
                <button
                  key={entry.tag.slug}
                  onClick={(e: React.MouseEvent) => {
                    const multi = e.metaKey || e.ctrlKey;
                    // If non-multi click on the only active tag, clear search entirely
                    if (!multi && isActive && activeTagSlugs.length === 1) {
                      clearSearch();
                    } else {
                      toggleTagFilter(entry.tag.slug, multi);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-colors text-left ${
                    isActive
                      ? 'bg-orange-500/20 text-orange-300'
                      : 'hover:bg-gray-800 text-gray-300'
                  }`}
                >
                  <span>{entry.tag.displayName}</span>
                  <span className={`font-medium tabular-nums ${isActive ? 'text-orange-400' : 'text-gray-500'}`}>
                    {entry.matchCount}
                  </span>
                </button>
              );
            })}

            {visibleEntries.length > 0 && (
              <div className="pt-1 text-[10px] text-gray-600">
                {tagProfile.taggedCount} tagged · {tagProfile.untaggedCount} with no tag data
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
